import React, { useState, useCallback, useEffect, useRef } from "react";
import "./../../../assets/css/general/FAQ/Faqpage.css";

// ── FAQ DATA ─────────────────────────────────────────────────────────────────
const FAQ_DATA = [
  {
    id: 1,
    category: "student",
    question: "How do I enroll in a course on Tech Mentor Hub?",
    answer:
      "Go to 'My Courses' from your dashboard, browse available courses by coding language, and click 'Enroll'. Once enrolled, you'll get access to the roadmap, assignments, and can request a mentor for that course.",
  },
  {
    id: 2,
    category: "student",
    question: "How can I select a mentor for my course?",
    answer:
      "Visit 'My Mentors' section and browse available mentors along with their course specializations and availability. Click 'Send Request' on a mentor's profile. Once the mentor accepts, you'll be connected and can start chatting.",
  },
  {
    id: 3,
    category: "student",
    question: "How does the Roadmap Progress section work?",
    answer:
      "Each course has a structured roadmap that outlines all topics in order. As you study, your progress is tracked visually — showing completed topics, your current topic, and upcoming milestones so you always know where you stand.",
  },
  {
    id: 4,
    category: "student",
    question: "What are Code Challenges and how do I participate?",
    answer:
      "Code Challenges is a gamified coding section. From your dashboard you'll see 4 challenge cards — click 'Start' to enter the Code Arena and solve real-time coding problems. Your performance contributes to your AI Leaderboard ranking.",
  },
  {
    id: 5,
    category: "student",
    question: "How do I check my assignments and their status?",
    answer:
      "Go to 'My Assignments' and select a subject. All assignments for that subject will appear with a clear status label — Completed or Pending — so you never miss a deadline.",
  },
  {
    id: 6,
    category: "student",
    question: "Can I report a mentor if I have an issue?",
    answer:
      "Yes. Go to 'My Mentors', find the mentor's profile, and use the 'Report' button. Your report will be reviewed by the Admin, who can take appropriate action including deactivating the mentor's account.",
  },
  {
    id: 7,
    category: "mentor",
    question: "How do I get verified as a mentor on Tech Mentor Hub?",
    answer:
      "Sign up and submit your mentor application with your professional details, bio, and areas of expertise. The Admin will review your application — you can track its status (Pending, Under Review, or Approved) through your dashboard.",
  },
  {
    id: 8,
    category: "mentor",
    question: "How do I manage student mentorship requests?",
    answer:
      "In your dashboard go to 'Mentorship Requests'. You'll see all incoming requests with student details and the course they need help with. You can Accept or Reject requests, and monitor pending requests at any time.",
  },
  {
    id: 9,
    category: "mentor",
    question: "How do I create and assign tasks to students?",
    answer:
      "Navigate to the 'Assignments' section, create an assignment, and assign it to a specific student under a course. You can also track which assignments have been given to which students from the same section.",
  },
  {
    id: 10,
    category: "mentor",
    question: "Can I suggest a course roadmap to the Admin?",
    answer:
      "Yes! In the 'Roadmap Suggestions' section, you can create a structured roadmap for a course and submit it to the Admin for review and approval. The Admin can then adopt it as the official roadmap for that course.",
  },
  {
    id: 11,
    category: "mentor",
    question: "How do I manage my availability for sessions?",
    answer:
      "In 'Mentor Profile', under the Availability section, you can set which days you're available for sessions and specify session durations. Students and Admins will see your availability when scheduling.",
  },
  {
    id: 12,
    category: "mentor",
    question: "How do I view and answer student questions?",
    answer:
      "The 'Student Questions' section shows all questions asked by your students. You'll see which questions have been answered and which are still pending — allowing you to prioritize and respond efficiently.",
  },
  {
    id: 13,
    category: "admin",
    question: "What does the Admin dashboard show?",
    answer:
      "The Admin dashboard gives a complete overview of platform activity — total users, active courses, pending mentor verifications, and recent system events. Admins can also send system-wide announcements from this page.",
  },
  {
    id: 14,
    category: "admin",
    question: "How does the Admin manage users?",
    answer:
      "In 'User Management', Admins can view full details of all mentors and students. They cannot add or delete accounts, but they can Activate or Deactivate a user based on reports received from students or mentors.",
  },
  {
    id: 15,
    category: "admin",
    question: "How does Admin verify a mentor application?",
    answer:
      "Go to 'Mentor Verifications' to see all applications sorted by status — Pending, Under Review, or Approved. Review the mentor's credentials and either approve them or place them under review for further checks.",
  },
  {
    id: 16,
    category: "admin",
    question: "How does the Admin manage courses and roadmaps?",
    answer:
      "In 'Courses', Admins can create and edit available courses. In 'Roadmap Management', Admins can create, edit, and publish roadmaps, and also review roadmap suggestions submitted by mentors — setting them as Active or Draft.",
  },
  {
    id: 17,
    category: "ai",
    question: "What is the AI Tutor and how does it help me?",
    answer:
      "The AI Tutor is your 24/7 smart assistant. You can ask it academic and coding questions, get instant explanations, request hints for coding problems, and receive feedback on your code. It also creates personalized coding challenges and evaluates your performance.",
  },
  {
    id: 18,
    category: "ai",
    question: "What kind of questions can I ask the AI Tutor?",
    answer:
      "You can ask about any programming concept (Python, JavaScript, etc.), request code explanations, ask for debugging help, or ask for a step-by-step walkthrough of a topic. The AI supports learning through your entire course roadmap.",
  },
  {
    id: 19,
    category: "ai",
    question: "How does the AI Leaderboard work?",
    answer:
      "The AI Leaderboard ranks students based on their performance in Code Challenges, consistency, and overall progress. It's updated in real time to keep learning competitive and motivating.",
  },
  {
    id: 20,
    category: "ai",
    question: "Can the AI create coding challenges for me?",
    answer:
      "Yes! The AI Tutor can generate personalized coding challenges based on your current level and course topic. After you attempt a challenge, the AI evaluates your solution and gives instant feedback and a score.",
  },
  {
    id: 21,
    category: "ai",
    question: "Is the AI Tutor available 24/7?",
    answer:
      "Yes! The AI Tutor is always available — day or night. You never have to wait for a mentor to respond for quick conceptual questions or coding help.",
  },
];

const CATEGORIES = ["all", "student", "mentor", "admin", "ai"];
const CATEGORY_LABELS = {
  all: "All Topics",
  student: "Students",
  mentor: "Mentors",
  admin: "Admins",
  ai: "AI Features",
};
const CATEGORY_ICONS = { student: "🎓", mentor: "👨‍💻", admin: "⚙️", ai: "🤖" };
const TOPIC_PILLS = [
  "AI Tutor",
  "Roadmap",
  "Assignments",
  "Mentor",
  "Courses",
  "Leaderboard",
];

// ── Highlight match ───────────────────────────────────────────────────────────
const highlight = (text, q) => {
  if (!q) return text;
  const regex = new RegExp(
    `(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
    "gi",
  );
  return text.replace(regex, '<mark class="faq-hl">$1</mark>');
};

// ── FaqPage ───────────────────────────────────────────────────────────────────
const FaqPage = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const answerRefs = useRef({});

  const filtered = FAQ_DATA.filter((f) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      f.question.toLowerCase().includes(q) ||
      f.answer.toLowerCase().includes(q);
    const matchCat = activeTab === "all" || f.category === activeTab;
    return matchSearch && matchCat;
  });

  const toggle = useCallback(
    (id) => setOpenId((prev) => (prev === id ? null : id)),
    [],
  );

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setOpenId(null);
    if (e.target.value) setActiveTab("all");
  };

  const handlePill = (pill) => {
    setSearch(pill);
    setActiveTab("all");
    setOpenId(null);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearch("");
    setOpenId(null);
  };

  const countFor = (cat) =>
    FAQ_DATA.filter((f) => cat === "all" || f.category === cat).length;

  return (
    <div className="faq-page">
      {/* ══ HERO ══ */}
      <div className="faq-hero">
        <div className="faq-hero-inner">
          <p className="faq-hero-tag">Help Center</p>
          <h1 className="faq-hero-title">Frequently Asked Questions</h1>
          <p className="faq-hero-sub">
            Find answers about courses, mentors, AI tutor, assignments, and
            everything on Tech Mentor Hub.
          </p>

          {/* Search */}
          <div className="faq-search-wrap">
            <span className="faq-search-icon">🔍</span>
            <input
              type="text"
              className="faq-search-input"
              placeholder='Try "AI Tutor", "assignments", "roadmap"…'
              value={search}
              onChange={handleSearch}
            />
            {search && (
              <button
                className="faq-search-clear"
                onClick={() => {
                  setSearch("");
                  setOpenId(null);
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Pills */}
          <div className="faq-pills">
            <span className="faq-pills-label">Popular:</span>
            {TOPIC_PILLS.map((pill) => (
              <button
                key={pill}
                className={`faq-pill ${search === pill ? "faq-pill--active" : ""}`}
                onClick={() => handlePill(pill)}
              >
                {pill}
              </button>
            ))}
          </div>
        </div>

        {/* Decorative blobs */}
        <div className="faq-blob faq-blob1" />
        <div className="faq-blob faq-blob2" />
      </div>

      {/* ══ TABS ══ */}
      <div className="faq-tabs-wrap">
        <div className="faq-tabs">
          {CATEGORIES.map((tab) => (
            <button
              key={tab}
              className={`faq-tab ${activeTab === tab ? "faq-tab--active" : ""}`}
              onClick={() => switchTab(tab)}
            >
              {tab !== "all" && (
                <span className="faq-tab-icon">{CATEGORY_ICONS[tab]}</span>
              )}
              {CATEGORY_LABELS[tab]}
              <span className="faq-tab-count">{countFor(tab)}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ══ ACCORDION ══ */}
      <div className="faq-body">
        {CATEGORIES.filter((c) => c !== "all").map((cat) => {
          const items = filtered.filter((f) => f.category === cat);
          if ((activeTab !== "all" && activeTab !== cat) || items.length === 0)
            return null;

          return (
            <div key={cat} className="faq-section">
              {/* Section header */}
              <div className="faq-section-head">
                <span className="faq-section-icon">{CATEGORY_ICONS[cat]}</span>
                <span className="faq-section-label">
                  {CATEGORY_LABELS[cat]}
                </span>
                <span className="faq-section-count">
                  {items.length} question{items.length !== 1 ? "s" : ""}
                </span>
              </div>

              {/* Items */}
              <div className="faq-list">
                {items.map((faq, idx) => {
                  const isOpen = openId === faq.id;
                  return (
                    <div
                      key={faq.id}
                      className={`faq-item ${isOpen ? "faq-item--open" : ""}`}
                    >
                      <button
                        className="faq-question"
                        onClick={() => toggle(faq.id)}
                        aria-expanded={isOpen}
                      >
                        <span className="faq-q-num">{idx + 1}</span>
                        <span
                          className="faq-q-text"
                          dangerouslySetInnerHTML={{
                            __html: highlight(faq.question, search),
                          }}
                        />
                        <span
                          className={`faq-chevron ${isOpen ? "faq-chevron--open" : ""}`}
                        >
                          ›
                        </span>
                      </button>

                      <div
                        className={`faq-answer ${isOpen ? "faq-answer--open" : ""}`}
                      >
                        <div
                          className="faq-answer-text"
                          dangerouslySetInnerHTML={{
                            __html: highlight(faq.answer, search),
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* No results */}
        {search && filtered.length === 0 && (
          <div className="faq-empty">
            <span className="faq-empty-icon">🔍</span>
            <p className="faq-empty-title">
              No results for "<strong>{search}</strong>"
            </p>
            <p className="faq-empty-sub">
              Try a different keyword or browse a category above.
            </p>
          </div>
        )}
      </div>

      {/* ══ BOTTOM CARDS ══ */}
      <div className="faq-bottom">
        {[
          {
            icon: "💬",
            title: "Chat with Your Mentor",
            desc: "Still have questions? Your assigned mentor is here to guide you personally.",
            link: "/chat",
            cta: "Open Chat →",
          },
          {
            icon: "🤖",
            title: "Ask the AI Tutor",
            desc: "Get instant answers on any coding concept — available 24/7 in your dashboard.",
            link: "/ask-ai",
            cta: "Ask AI Now →",
          },
          {
            icon: "✉️",
            title: "Contact Support",
            desc: "Can't find what you're looking for? Our support team is ready to help.",
            link: "/contact",
            cta: "Get in Touch →",
          },
        ].map((card) => (
          <div key={card.title} className="faq-bottom-card">
            <span className="faq-bottom-icon">{card.icon}</span>
            <h5 className="faq-bottom-title">{card.title}</h5>
            <p className="faq-bottom-desc">{card.desc}</p>
            <a href={card.link} className="faq-bottom-link">
              {card.cta}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FaqPage;
